import { CreateBucketCommand, DeleteBucketCommand, DeleteObjectCommand, GetObjectCommand, HeadObjectCommand, ListObjectsV2Command, NoSuchKey, NotFound, PutObjectCommand, S3Client, S3ServiceException, StorageClass as AwsStorageClass, } from '@aws-sdk/client-s3';
import { assertCanHandle, isNil, isNilOrEmpty } from '@lucidlink/tscore';
import { Logging } from '#common/logging';
import { withStats } from '#common/stats';
import { InternalError, InvalidArgumentError, TimeoutError } from '#common/types';
import { waitForCondition } from '#common/util';
import { isKnownReliableProvider, ObjectStoreHelper } from '#object-store/common';
import { AuthorizationError, AWS_PROVIDER, BucketNotEmptyError, BucketNotFoundError, IBM_PROVIDER, ObjectNotFoundError, ObjectStoreCredentialsKind, ObjectStoreError, ObjectStoreStats, StorageClass, } from '#object-store/types';
import { createS3ClientConfig } from './client-config';
import { IamCredentialsProvider } from './iam-credentials-provider';
import { US_EAST_REGION } from './region';
import { DEFAULT_RETRY_CONFIG, RetryStrategy } from './retry';
import { createS3AuthorizationError } from './s3-authorization-error';
import { StsCredentialsProvider } from './sts-credentials-provider';
const createCredentialsProvider = async (config) => {
    if (!isNil(config.stsContext)) {
        const credentialsProvider = new StsCredentialsProvider(config.stsContext.stsConfigFetcher);
        await credentialsProvider.setUp();
        return credentialsProvider;
    }
    if (config.credentials.kind !== ObjectStoreCredentialsKind.IAM) {
        throw new InternalError('Expected IAM credentials for non-STS S3 configuration');
    }
    return new IamCredentialsProvider(config.credentials);
};
export const createS3Store = async (config, retryOptions = DEFAULT_RETRY_CONFIG) => {
    validateConfig(config);
    const credentialsProvider = await createCredentialsProvider(config);
    return new S3Store(config, credentialsProvider, isKnownReliableProvider(config.provider) ? RetryStrategy.DEFAULT : RetryStrategy.ALL, retryOptions);
};
function validateConfig(config) {
    if (isNilOrEmpty(config.region) && isNilOrEmpty(config.endpointOverride)) {
        throw new InvalidArgumentError('Endpoint or region should be present in the object store config!');
    }
}
class S3Store {
    providerConfig;
    credentialsProvider;
    log = Logging.getLogger('S3Store');
    statistics = new ObjectStoreStats(this.log);
    s3Config;
    s3Client;
    constructor(providerConfig, credentialsProvider, retryStrategy, retryOptions) {
        this.providerConfig = providerConfig;
        this.credentialsProvider = credentialsProvider;
        this.s3Config = createS3ClientConfig({
            credentialsProvider,
            endpointOverride: providerConfig.endpointOverride,
            region: providerConfig.region,
            useVirtualAddressing: providerConfig.useVirtualAddressing,
            httpScheme: providerConfig.httpScheme,
            retryStrategy,
            retryOptions,
        });
        this.s3Client = new S3Client(this.s3Config);
    }
    async bucketExists(bucketName, objectPrefix) {
        const doExist = await this.bucketExistsImpl(bucketName, objectPrefix);
        // NOTE: workaround to prevent hang of the next request when using non-AWS providers (including MinIO with HTTPS).
        // Requires more investigation. There's a chance this only happens if the bucket does not exist.
        // In order to verify this, we need S3 credentials for plenty of providers.
        const provider = this.providerConfig.provider.toLowerCase();
        if (provider !== AWS_PROVIDER) {
            this.s3Client = new S3Client(this.s3Config);
        }
        return doExist;
    }
    async createBucket(bucketName, objectPrefix) {
        await withStats(this.statistics.createBucket, async () => {
            try {
                await this.s3Client.send(new CreateBucketCommand({
                    Bucket: bucketName,
                    CreateBucketConfiguration: {
                        LocationConstraint: this.getLocationConstraint(),
                    },
                }));
            }
            catch (error) {
                this.handleS3Error(error, bucketName, '');
            }
        }, { bucketName });
        await this.ensureBucketExists(bucketName, objectPrefix);
    }
    async deleteBucket(bucketName, objectPrefix) {
        return withStats(this.statistics.deleteBucket, async () => {
            await this.tryDeleteSysKey(bucketName, objectPrefix);
            try {
                await this.s3Client.send(new DeleteBucketCommand({ Bucket: bucketName }));
            }
            catch (error) {
                this.handleS3Error(error, bucketName, '');
            }
        }, { bucketName, objectPrefix });
    }
    async headObject(bucketName, objectKey) {
        return this.headObjectImpl(bucketName, objectKey, true);
    }
    async getObject(bucketName, objectKey, offset, size) {
        return withStats(this.statistics.getObject, async () => {
            try {
                const lastByte = Number(offset) + Number(size) - 1;
                const range = 'bytes=' + offset.toString() + '-' + lastByte.toString();
                const response = await this.s3Client.send(new GetObjectCommand({
                    Bucket: bucketName,
                    Key: objectKey,
                    Range: range,
                    ResponseCacheControl: 'no-store',
                }));
                return response.Body.transformToByteArray();
            }
            catch (error) {
                await this.handleHeadGetObjectError(error, bucketName, objectKey);
                throw new InternalError('Unexpected error during getObject');
            }
        }, { bucketName, objectKey, offset, size });
    }
    async putObject(bucketName, objectKey, data) {
        return withStats(this.statistics.putObject, async () => {
            const putParams = { Bucket: bucketName, Key: objectKey, Body: data };
            const awsStorageClass = this.getStorageClass();
            if (!isNil(awsStorageClass)) {
                putParams.StorageClass = awsStorageClass;
            }
            try {
                await this.s3Client.send(new PutObjectCommand(putParams));
            }
            catch (error) {
                this.handleS3Error(error, bucketName, objectKey);
                throw new InternalError('Unexpected error during putObject');
            }
        }, { bucketName, objectKey, size: data.length });
    }
    async deleteObject(bucketName, objectKey) {
        return withStats(this.statistics.deleteObject, async () => {
            try {
                await this.s3Client.send(new DeleteObjectCommand({ Bucket: bucketName, Key: objectKey }));
            }
            catch (error) {
                this.handleS3Error(error, bucketName, objectKey);
                throw new InternalError('Unexpected error during deleteObject');
            }
        }, { bucketName, objectKey });
    }
    async listObjects(bucketName, params) {
        return withStats(this.statistics.listObjects, async () => {
            try {
                const response = await this.s3Client.send(new ListObjectsV2Command({
                    Bucket: bucketName,
                    Prefix: params.prefix,
                    Delimiter: params.delimiter,
                    ContinuationToken: params.continuationToken,
                    MaxKeys: params.maxKeys,
                }));
                return S3Store.toObjectListingPage(response, params.prefix);
            }
            catch (error) {
                this.handleS3Error(error, bucketName, '');
                throw new InternalError('Unexpected error during listObjects');
            }
        }, { bucketName, ...params });
    }
    static toObjectListingPage(response, prefix) {
        const delimiter = response.Delimiter;
        const folders = (response.CommonPrefixes ?? [])
            .map((commonPrefix) => commonPrefix.Prefix)
            .filter((commonPrefix) => !isNilOrEmpty(commonPrefix))
            .map((commonPrefix) => ({ key: commonPrefix, size: 0, lastModTime: 0, isFolder: true }));
        const files = (response.Contents ?? [])
            .filter((object) => !isNilOrEmpty(object.Key) && !S3Store.isListedFolderPlaceholder(object.Key, prefix, delimiter))
            .map((object) => ({
            key: object.Key,
            size: object.Size ?? 0,
            lastModTime: object.LastModified ? object.LastModified.getTime() : 0,
            isFolder: false,
        }));
        const listing = { objects: [...folders, ...files] };
        if (response.IsTruncated && !isNilOrEmpty(response.NextContinuationToken)) {
            listing.nextContinuationToken = response.NextContinuationToken;
        }
        return listing;
    }
    // S3 has no directories: a folder made through a console or a tool is a zero-byte object whose key ends with the delimiter.
    static isListedFolderPlaceholder(key, prefix, delimiter) {
        return key === prefix && !isNilOrEmpty(delimiter) && key.endsWith(delimiter);
    }
    stats() {
        return this.statistics;
    }
    stop() {
        this.credentialsProvider.stop();
        this.s3Client.destroy();
    }
    async headObjectImpl(bucketName, objectKey, deduceNonExistingObjectError) {
        return withStats(this.statistics.headObject, async () => {
            try {
                const head = await this.s3Client.send(new HeadObjectCommand({ Bucket: bucketName, Key: objectKey }));
                return {
                    objectKey: objectKey,
                    lastModTime: head.LastModified ? head.LastModified.getTime() : 0,
                    objectSize: head.ContentLength ? head.ContentLength : 0,
                };
            }
            catch (error) {
                await this.handleHeadObjectError(error, objectKey, deduceNonExistingObjectError, bucketName);
                throw new InternalError('Unexpected error during headObject');
            }
        }, { bucketName, objectKey, deduceNonExistingObjectError });
    }
    async handleHeadObjectError(error, objectKey, deduceNonExistingObjectError, bucketName) {
        // NOTE: we cannot distinguish bucket/object not found
        if (S3Store.isNotFoundError(error)) {
            throw new ObjectNotFoundError(objectKey);
        }
        if (deduceNonExistingObjectError) {
            await this.handleHeadGetObjectError(error, bucketName, objectKey);
        }
        else {
            this.handleS3Error(error, bucketName, objectKey);
        }
    }
    async bucketExistsImpl(bucketName, objectPrefix) {
        const isBucketNotFound = (error) => {
            return error instanceof BucketNotFoundError;
        };
        const sysKey = ObjectStoreHelper.generateLucidSysKey(objectPrefix);
        return withStats(this.statistics.bucketExists, async () => {
            try {
                await this.headObjectImpl(bucketName, sysKey, false);
                return true;
            }
            catch (_) { }
            try {
                await this.putObject(bucketName, sysKey, new Uint8Array());
                return true;
            }
            catch (error) {
                assertCanHandle(error, isBucketNotFound);
                return false;
            }
        }, { bucketName, objectPrefix });
    }
    async tryDeleteSysKey(bucketName, objectPrefix) {
        const sysKey = ObjectStoreHelper.generateLucidSysKey(objectPrefix);
        try {
            await this.deleteObject(bucketName, sysKey);
        }
        catch (error) {
            assertCanHandle(error, S3Store.isObjectNotFoundError);
        }
    }
    async handleHeadGetObjectError(error, bucketName, objectKey) {
        const isAuthorizationError = (error) => {
            return error instanceof AuthorizationError;
        };
        try {
            this.handleS3Error(error, bucketName, objectKey);
        }
        catch (error) {
            assertCanHandle(error, isAuthorizationError);
            const objectPrefix = ObjectStoreHelper.keyPrefix(objectKey);
            // When we don't have ListBucket permission head object of non-existing object
            // returns 403. To differentiate between not having permission to the bucket or
            // the object does not exist call BucketExists
            if (await this.bucketExists(bucketName, objectPrefix)) {
                throw new ObjectNotFoundError(objectKey);
            }
            throw error;
        }
    }
    handleS3Error(error, bucketName, objectKey) {
        if (S3Store.isAuthorizationError(error)) {
            throw createS3AuthorizationError(`Authorization exception: ${error.name}`, error.name);
        }
        if (S3Store.isBucketNotFoundError(error)) {
            throw new BucketNotFoundError(bucketName);
        }
        if (S3Store.isBucketNotEmptyError(error)) {
            throw new BucketNotEmptyError(bucketName);
        }
        if (S3Store.isObjectNotFoundError(error)) {
            throw new ObjectNotFoundError(objectKey);
        }
        throw error;
    }
    static isBucketNotFoundError = (error) => {
        return error instanceof S3ServiceException && error.name == 'NoSuchBucket';
    };
    static isBucketNotEmptyError = (error) => {
        return error instanceof S3ServiceException && error.name == 'BucketNotEmpty';
    };
    static isAuthorizationError = (error) => {
        return error instanceof S3ServiceException && error.$metadata.httpStatusCode === 403;
    };
    static isObjectNotFoundError = (error) => {
        return error instanceof NoSuchKey;
    };
    static isNotFoundError = (error) => {
        return error instanceof NotFound;
    };
    getStorageClass() {
        switch (this.providerConfig.storageClass) {
            case StorageClass.STANDARD:
                return AwsStorageClass.STANDARD;
            case StorageClass.INTELLIGENT_TIERING:
                return AwsStorageClass.INTELLIGENT_TIERING;
            case undefined:
                return undefined;
            default:
                throw new InternalError(`Unknown storage type: ${this.providerConfig.storageClass}`);
        }
    }
    getLocationConstraint() {
        let locationConstraint;
        const provider = this.providerConfig.provider.toLowerCase();
        if (provider === AWS_PROVIDER && this.providerConfig.region === US_EAST_REGION) {
            // AWS-specific case - the us-east-1 region works only without location constraint.
            locationConstraint = '';
        }
        else if (provider === IBM_PROVIDER) {
            // Intentionally ignore the this.providerConfig.setLocationConstraint parameter.
            // The C++ SDK works without location constraint for IBM, but the TypeScript SDK fails for some reason
            // (complains about wrong location constraint parameter during CreateBucket operations).
            // Explicitly setting the location constraint seems to work fine.
            // Another IBM perk is - we need to specify the storage class as a part of this parameter.
            // Currently, we only support standard storage class for IBM, so hard-coding it here.
            locationConstraint = this.providerConfig.region + '-standard';
        }
        else if (this.providerConfig.setLocationConstraint) {
            locationConstraint = this.providerConfig.region;
        }
        else {
            locationConstraint = '';
        }
        return locationConstraint;
    }
    async ensureBucketExists(bucketName, objectPrefix) {
        const numIterations = 10;
        const sleepTimeMs = 1000;
        try {
            await waitForCondition(() => {
                return this.bucketExists(bucketName, objectPrefix);
            }, numIterations, sleepTimeMs);
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof TimeoutError);
            throw new ObjectStoreError('Timed out while waiting for the bucket to get created.');
        }
    }
}
